import { useEffect } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';

const KansasCity = () => {
  useEffect(() => {
    // Set document title
    document.title = 'We Buy Valves Kansas City | Industrial Equipment Buyers Kansas City';
    
    // Set meta tags
    const metaTags = [
      { name: 'description', content: 'We buy valves Kansas City - Professional valve buyers serving metro area manufacturing plants. We purchase surplus valves, control systems, and instrumentation at above scrap rates. Call 214-854-5638.' },
      { name: 'keywords', content: 'we buy valves kansas city, valve buyers Kansas City, manufacturing equipment, surplus valves, Fisher control valves, food processing equipment' },
      { property: 'og:title', content: 'We Buy Valves Kansas City | Industrial Equipment Buyers Kansas City' },
      { property: 'og:description', content: 'We buy valves Kansas City - Professional valve buyers serving metro area manufacturing plants and processing facilities.' },
      { property: 'og:url', content: 'https://valvesalessupply.com/kansas-city' },
      { property: 'og:type', content: 'website' },
      { name: 'geo.position', content: '39.0997;-94.5786' },
      { name: 'geo.placename', content: 'Kansas City, Kansas' },
      { name: 'geo.region', content: 'US-KS' }
    ];

    metaTags.forEach(tag => {
      const existingTag = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (existingTag) {
        existingTag.setAttribute('content', tag.content);
      } else {
        const meta = document.createElement('meta');
        if (tag.name) meta.setAttribute('name', tag.name);
        if (tag.property) meta.setAttribute('property', tag.property);
        meta.setAttribute('content', tag.content);
        document.head.appendChild(meta);
      }
    });

    // Set canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://valvesalessupply.com/kansas-city');

    // Add schema.org structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://valvesalessupply.com/kansas-city",
          "url": "https://valvesalessupply.com/kansas-city",
          "name": "We Buy Valves Kansas City | Industrial Equipment Buyers Kansas City",
          "description": "We buy valves Kansas City - Professional valve buyers serving metro area manufacturing plants. We purchase surplus valves, control systems, and instrumentation at above scrap rates. Call 214-854-5638.",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://valvesalessupply.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Kansas",
                "item": "https://valvesalessupply.com/kansas"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Kansas City",
                "item": "https://valvesalessupply.com/kansas-city"
              }
            ]
          }
        },
        {
          "@type": "LocalBusiness",
          "name": "Valve Sales Supply - Kansas City",
          "image": "https://valvesalessupply.com/logo.png",
          "@id": "https://valvesalessupply.com/kansas-city",
          "url": "https://valvesalessupply.com/kansas-city",
          "telephone": "214-854-5638",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Kansas City",
            "addressRegion": "KS",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 39.0997,
            "longitude": -94.5786
          },
          "areaServed": {
            "@type": "City",
            "name": "Kansas City"
          },
          "serviceType": ["Industrial Valve Buyers", "Manufacturing Equipment Buyers", "Food Processing Equipment", "Chemical Plant Equipment"]
        },
        {
          "@type": "FAQPage",
          "mainEntity": faqData.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        }
      ]
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schemaData);

    // Cleanup function
    return () => {
      document.title = 'Valve Sales Supply';
    };
  }, []);

  const nearbyFacilities = [
    { name: 'Manufacturing Plants', type: 'Industrial', capacity: '300+ Facilities' },
    { name: 'Food Processing', type: 'Processing', capacity: 'Major Plants' },
    { name: 'Distribution Centers', type: 'Logistics', capacity: '150+ Centers' },
    { name: 'Chemical Plants', type: 'Chemical', capacity: 'Multiple Sites' }
  ];

  const faqData = [
    {
      question: "Do you buy valves from Kansas City manufacturing plants?",
      answer: "Yes, we purchase surplus valves, Fisher control valves, and instrumentation from all Kansas City manufacturing plants, food processing facilities, and chemical plants. We provide 4-6 hour response times with competitive pricing for the metro area."
    },
    {
      question: "What types of equipment do you buy in Kansas City?",
      answer: "We buy control valves, ball valves, gate valves, butterfly valves, heat exchangers, pumps, compressors, safety systems, Rosemount transmitters, flow meters, pressure instruments, and controllers from Kansas City industrial facilities."
    },
    {
      question: "How quickly can you respond to Kansas City facilities?",
      answer: "We provide 4-6 hour response times for Kansas City metro area facilities. Our Midwest team has 25+ years of experience serving the diverse industrial base in Kansas City and surrounding areas."
    },
    {
      question: "Do you purchase equipment from Kansas City food processing plants?",
      answer: "Yes, we buy process equipment and instrumentation from Kansas City's major food processing facilities. We understand the specific requirements of food-grade equipment and provide appropriate valuations."
    },
    {
      question: "What valve brands do you buy from Kansas City facilities?",
      answer: "We purchase Fisher, Masoneilan, Flowserve, Cameron, Rosemount, Emerson, Honeywell, and other major brands from Kansas City manufacturing plants, food processing facilities, and chemical plants."
    },
    {
      question: "Do you serve both Kansas and Missouri sides of Kansas City?",
      answer: "Yes, we serve the entire Kansas City metro area including both Kansas and Missouri facilities. Our team provides fast response times and professional service throughout the region."
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/47286e826862db940ef6340599322664.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B3A5F]/90 to-[#1B3A5F]/70"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-white">
          <div className="max-w-4xl">
            <h1 className="text-5xl font-bold mb-6">We Buy Valves Kansas City</h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Professional <strong>valve buyers in Kansas City</strong> serving the metro area's diverse industrial base. 
              Valve Sales Supply purchases surplus valves, control systems, and instrumentation from Kansas City manufacturing 
              plants and processing facilities at above scrap rates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:214-854-5638"
                className="bg-white text-[#1B3A5F] px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                CALL 214-854-5638
              </a>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#1B3A5F] transition-all duration-300 inline-flex items-center justify-center whitespace-nowrap"
              >
                GET KANSAS CITY QUOTE
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Market Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-[#1B3A5F] text-4xl font-bold mb-6">KANSAS CITY INDUSTRIAL EQUIPMENT BUYER</h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                Kansas City's strategic location makes it a major industrial and logistics hub for the Midwest. 
                As experienced <strong className="text-[#1B3A5F]">surplus valve buyers in Kansas City</strong>, 
                we provide professional equipment purchasing services for manufacturing and processing facilities.
              </p>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                We specialize in purchasing <strong className="text-[#1B3A5F]">Fisher control valves</strong>, 
                Rosemount transmitters, and complete instrumentation systems from Kansas City industrial facilities.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-[#1B3A5F] text-3xl font-bold">300+</div>
                  <div className="text-gray-600">Industrial Facilities</div>
                </div>
                <div className="text-center">
                  <div className="text-[#1B3A5F] text-3xl font-bold">4-6 Hrs</div>
                  <div className="text-gray-600">Response Time</div>
                </div>
                <div className="text-center">
                  <div className="text-[#1B3A5F] text-3xl font-bold">25 Years</div>
                  <div className="text-gray-600">Market Experience</div>
                </div>
              </div>
            </div>
            
            <div className="bg-[#F5F7FA] rounded-3xl p-8">
              <h3 className="text-[#1B3A5F] text-2xl font-bold mb-6">KANSAS CITY FACILITIES</h3>
              <div className="space-y-4">
                {nearbyFacilities.map((facility, index) => (
                  <div key={index} className="bg-white rounded-lg p-4">
                    <h4 className="text-[#1B3A5F] font-bold text-lg">{facility.name}</h4>
                    <div className="flex justify-between mt-2">
                      <span className="text-gray-600 text-sm">{facility.type}</span>
                      <span className="text-[#1B3A5F] font-semibold text-sm">{facility.capacity}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Categories */}
      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[#1B3A5F] text-4xl font-bold mb-4">SELL VALVES KANSAS CITY</h2>
            <p className="text-gray-600 text-xl">Professional equipment purchasing from Kansas City industrial facilities</p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Control Valves',
                equipment: ['Fisher Control Valves', 'Masoneilan Valves', 'Ball Valves', 'Gate Valves', 'Butterfly Valves'],
                description: 'Premium control valves from Kansas City facilities'
              },
              {
                title: 'Process Equipment',
                equipment: ['Heat Exchangers', 'Pumps', 'Compressors', 'Safety Systems'],
                description: 'Industrial equipment from manufacturing plants'
              },
              {
                title: 'Instrumentation',
                equipment: ['Rosemount Transmitters', 'Flow Meters', 'Pressure Instruments', 'Controllers'],
                description: 'Process instrumentation from metro area facilities'
              }
            ].map((category, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 hover:shadow-lg transition-all duration-300">
                <h3 className="text-[#1B3A5F] text-2xl font-bold mb-4">{category.title}</h3>
                <p className="text-gray-600 mb-6">{category.description}</p>
                
                <div className="space-y-3">
                  {category.equipment.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center">
                      <i className="ri-check-line text-[#1B3A5F] mr-3"></i>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-white rounded-3xl p-8">
            <h3 className="text-[#1B3A5F] text-2xl font-bold mb-8 text-center">VALVE BUYING PROCESS IN KANSAS CITY</h3>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: '1', title: 'Contact Us', desc: 'Call or email with your equipment list', icon: 'ri-phone-line' },
                { step: '2', title: 'Quick Evaluation', desc: 'We assess your valves and instrumentation', icon: 'ri-search-line' },
                { step: '3', title: 'Fair Offer', desc: 'Receive above-scrap pricing within hours', icon: 'ri-money-dollar-circle-line' },
                { step: '4', title: 'Fast Removal', desc: 'Professional pickup and documentation', icon: 'ri-truck-line' }
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-[#1B3A5F] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <div className="mb-3">
                    <i className={`${step.icon} text-[#1B3A5F] text-3xl`}></i>
                  </div>
                  <h4 className="text-[#1B3A5F] font-bold mb-2">{step.title}</h4>
                  <p className="text-gray-600 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Markets */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[#1B3A5F] text-4xl font-bold mb-4">RELATED MIDWEST MARKETS</h2>
            <p className="text-gray-600 text-xl">We serve all major Midwest industrial centers</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { city: 'Wichita', link: '/wichita', state: 'KS' },
              { city: 'Topeka', link: '/topeka', state: 'KS' },
              { city: 'Tulsa', link: '/tulsa', state: 'OK' },
              { city: 'Oklahoma City', link: '/oklahoma-city', state: 'OK' }
            ].map((market, index) => (
              <Link
                key={index}
                to={market.link}
                className="bg-[#F5F7FA] rounded-xl p-6 text-center hover:shadow-lg hover:bg-white transition-all duration-300 border-2 border-transparent hover:border-[#1B3A5F]"
              >
                <h3 className="text-[#1B3A5F] text-xl font-bold mb-2">{market.city}</h3>
                <div className="text-gray-600 text-sm mb-3">{market.state} Valve Buyers</div>
                <div className="text-[#1B3A5F] font-semibold inline-flex items-center">
                  View Details
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/kansas"
              className="text-[#1B3A5F] font-semibold hover:underline inline-flex items-center text-lg"
            >
              View All Kansas Cities
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-[#1B3A5F]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">KANSAS CITY EQUIPMENT BUYER</h2>
              <p className="text-xl text-gray-200 mb-8">
                Contact Mark Driver for professional equipment purchasing services in Kansas City
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <i className="ri-phone-line text-2xl mr-4"></i>
                  <div>
                    <div className="font-bold text-xl">214-854-5638</div>
                    <div className="text-gray-300">Direct Line - Mark Driver</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <i className="ri-mail-line text-2xl mr-4"></i>
                  <div>
                    <div className="font-bold">kansascity@valvesalessupply.com</div>
                    <div className="text-gray-300">Equipment Lists & Photos</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-8">
              <h3 className="text-[#1B3A5F] text-2xl font-bold mb-6">WHY CHOOSE VALVE SALES SUPPLY</h3>
              <div className="space-y-4">
                {[
                  'Above scrap rates for functional equipment',
                  'Fast 4-6 hour response in Kansas City',
                  'Expert knowledge of industrial equipment',
                  'Professional turnaround coordination',
                  'Complete compliance documentation',
                  '25+ years Midwest market experience'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center">
                    <i className="ri-check-line text-[#1B3A5F] text-xl mr-3"></i>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <Link
                  to="/contact"
                  className="bg-[#1B3A5F] text-white px-8 py-4 rounded-full font-bold hover:bg-[#1B3A5F]/90 transition-all duration-300 inline-flex items-center justify-center w-full whitespace-nowrap"
                >
                  GET QUOTE TODAY
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-[#1B3A5F] text-4xl font-bold mb-12 text-center">KANSAS CITY VALVE BUYERS FAQ</h2>
          <div className="space-y-6">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-[#F5F7FA] rounded-xl p-6">
                <h3 className="text-[#1B3A5F] text-xl font-bold mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KansasCity;
